import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { Waypoint } from 'react-waypoint';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { Topics } from '../../../../../models/LocalNotificationTopics';
import {
	invalidateUpcomingAutomationEmailsQuery,
	useInfiniteUpcomingAutomationEmailsQuery,
} from '../../../../../queries';
import { LoadingSpinner } from '../../../../../web/components/LoadingSpinner';
import { LocalNotificationObserver } from '../../../../../web/components/LocalNotificationObserver';
import { Portal } from '../../../../../web/components/Portal';
import { GroupedAutomations } from '../../../../../web/components/reporting/emails/GroupedAutomations';
import { styleSheet } from './styles';

export function AdminReportingAutomationsTable({
	accountId,
	scrollToBottomPortalId,
}: {
	accountId: string;
	scrollToBottomPortalId: string;
}) {
	const userSession = useUserSession();
	const impersonationContext = React.useMemo(() => ({ account: { id: accountId } }), [accountId]);
	const upcomingAutomationEmailsQuery = useInfiniteUpcomingAutomationEmailsQuery({
		impersonationContext,
	});
	const invalidateQuery = () => {
		invalidateUpcomingAutomationEmailsQuery({
			impersonationContext,
		});
	};
	const renderAutomationRows = () => {
		const automations = upcomingAutomationEmailsQuery.data.pages
			.map(page => page.values.map(aReport => new Api.AutomationReportViewModel(userSession, aReport)))
			.flat();
		return <GroupedAutomations items={automations} preventAutomationFunctionality={true} />;
	};

	return (
		<>
			{upcomingAutomationEmailsQuery.isPending ? (
				<div className={css(styleSheet.loader)}>
					<LoadingSpinner type='large' />
				</div>
			) : null}
			{upcomingAutomationEmailsQuery.status === 'success' ? (
				upcomingAutomationEmailsQuery.data.pages[0].values.length > 0 ? (
					<>
						<header className={css(styleSheet.header)}>
							<div className={css(styleSheet.headerItem)}>Email Subject Line</div>
							<div className={css(styleSheet.headerItem)}>Sent To (Tags or Individuals)</div>
							<div className={css(styleSheet.headerItem)}>Message Analytics</div>
						</header>
						{renderAutomationRows()}
						{upcomingAutomationEmailsQuery.hasNextPage && !upcomingAutomationEmailsQuery.isFetchingNextPage ? (
							<Portal destination={scrollToBottomPortalId}>
								<Waypoint bottomOffset='-200px' onEnter={() => upcomingAutomationEmailsQuery.fetchNextPage()} />
							</Portal>
						) : null}
					</>
				) : (
					<div className={css(styleSheet.noneFound)}>No automations found</div>
				)
			) : null}
			<LocalNotificationObserver
				topic={Topics.CREATE_CAMPAIGNS_ITEM}
				onNotificationReceived={() => invalidateQuery()}
			/>
		</>
	);
}
